import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Pagination } from "flowbite-react";
import useCreadyApi from "../utils/Api";
import FortnoxTable from "../components/FortnoxTable";
import Modal from "../components/Modal";
import { Cog6ToothIcon, CheckIcon, CloudArrowDownIcon } from "@heroicons/react/24/outline";

export default function FortnoxList() {
  const tabs = [
    { name: "Hämtade fakturor", id: 0 },
    /*tab with id 2 is not visible */
    { name: "Skickade till inkasso", id: 2 },
    { name: "Fel vid inläsning", id: 3 },
    { name: "Avvaktade fakturor", id: 4 },
  ];

  const [fortnoxList, setFortnoxList] = useState({ items: [], total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listCounts, setListCounts] = useState({ added: 0, ignored: 0 });
  const [userInteracted, setUserInteracted] = useState(false);
  const navigate = useNavigate();

  const api = useCreadyApi();

  useEffect(() => {
    let params = getAllSearchParams();
    setCurrentPage(params.page ?? 1);
    setActiveTab(getItemById(params.active ?? 0, tabs) || tabs[0]);
    search();
  }, []);

  useEffect(() => {
    if (!isLoading && userInteracted) search();
  }, [activeTab]);

  useEffect(() => {
    if (!isLoading && userInteracted) search();
  }, [currentPage]);

  useEffect(() => {
    countList();
  })

  function getAllSearchParams() {
    let active = searchParams.get("active");
    let page = searchParams.get("page") ?? 1;
    return { page, active };
  }

  function getItemById(id, list) {
    return list.find((a) => a.id === parseInt(id)) ?? null;
  }

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  async function search() {
    setIsLoading(true);
    try {
      let params = [{ page: currentPage }, { statuses: activeTab.id }];
      const responseData = await api.fortnox.list(params);
      setFortnoxList(responseData || { items: [], total: 0 });
    } catch (error) {
      console.error("Error fetching data:", error);
      setFortnoxList({ items: [], total: 0 });
    } finally {
      setIsLoading(false);
    }
  }

  const onFilterChange = (name, value) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(name, value);
    setSearchParams(searchParams.toString());
  };

  const onPageChange = (page) => {
    onFilterChange("page", page);
    setCurrentPage(page);
    setUserInteracted(true);
  };

  function handleTabChange(tab) {
    onFilterChange("active", tab.id);
    onFilterChange("page", 1);
    setCurrentPage(1);
    setActiveTab(tab);
    setUserInteracted(true);
  }

  async function fetchNew() {
    const response = await api.fortnox.fetch();
    if (response.new === 0) {alert(`Det finns inga nya obetalda fakturor att hämta`);}
      else {alert(`Hämtade ${response.new} nya fakturor`);}    
    window.location.reload();
  }

  const countList = () => {
    const addedRows = JSON.parse(localStorage.getItem("addedRows")) || [];
    const ignoredRows = JSON.parse(localStorage.getItem("ignoredRows")) || [];
    setListCounts({ added: addedRows.length, ignored: ignoredRows.length });
  }

  const openSaveModal = () => {
    countList();
    setIsModalOpen(true);
  };

  const handleSaveContinue = async () => {
    await api.fortnox.process({
      invoicesToVizion: JSON.parse(localStorage.getItem("addedRows")) || [],
      invoicesToIgnore: JSON.parse(localStorage.getItem("ignoredRows")) || [],
    });
    setIsModalOpen(false);

    localStorage.removeItem("addedRows");
    localStorage.removeItem("ignoredRows");
    window.location.reload();
  };

  const handleSaveCancel = () => {
    setIsModalOpen(false);
  };

  const goToSettings = () => {
    navigate('/settings', {
      state: { showSettings: true }
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex justify-between mt-4  sm:mt-0 sm:flex-none items-start">

          <button onClick={goToSettings} aria-label="Inställningar" className="block group flex items-center px-3 py-2.5 mb-4 text-sm font-semibold text-center text-darkgrey hover:bg-darkgrey align-middle transition-all duration-100 ease-in-out bg-transparent border-2 border-darkgrey border-solid rounded-md cursor-pointer hover:text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-1">
            <Cog6ToothIcon aria-hidden="true" stroke="black" stroke-width="1.5" className="h-6 w-6 group-hover:stroke-white transition-all duration-100 ease-in-out"/>
          </button>
          <div className="sm:flex gap-5 mt-4 sm:ml-6 sm:mt-0 sm:flex-none">
            <button
              className="block group flex items-center px-4 pl-3 py-2.5 mb-4 text-sm font-semibold text-center text-blue-1 hover:bg-blue-1 align-middle transition-all duration-100 ease-in-out bg-transparent border-2 border-blue-1 border-solid rounded-md cursor-pointer hover:text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-1"
              onClick={fetchNew}
            >
              <CloudArrowDownIcon aria-hidden="true" stroke-width="1.5" className="mr-2 h-6 w-6 stroke-blue-1 group-hover:stroke-white transition-all duration-100 ease-in-out"/> Hämta fakturor från Fortnox
            </button>
            <button
              className={`group w-48 flex items-center px-4 pl-4 py-2.5 mb-4 text-sm font-semibold text-center hover:text-blue-1 ${(listCounts.added + listCounts.ignored) >0 ? 'bg-blue-1 text-white' : ''}  text-blue-1 transition-all duration-100 ease-in-out hover:bg-transparent border-2 border-blue-1 border-solid rounded-md cursor-pointer shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-1`}
              onClick={openSaveModal}
            >
             <CheckIcon aria-hidden="true" stroke-width="1.5" className={`mr-2 h-6 w-6 transition-all duration-100 ease-in-out ${(listCounts.added + listCounts.ignored) >0 ? 'stroke-white group-hover:stroke-blue-1' : ''}`} /> Utför {((listCounts.added + listCounts.ignored) >0 ? listCounts.added + listCounts.ignored : '0')} åtgärder
            </button>
          </div>
      </div>
 
      <div className="mt-4">
        {/* Mobile view */}
        <div className="sm:hidden">
          <select
            value={activeTab.id}
            onChange={(e) => handleTabChange(tabs.find(tab => tab.id === parseInt(e.target.value)))}
            className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 focus:border-blue-1 focus:outline-none focus:ring-blue-1 sm:text-sm"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop view */}
        <div className="hidden sm:block">
          <div className="">
            <nav className="-mb-px flex space-x-3" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => handleTabChange(tab)}
                  className={classNames(
                    tab.id === activeTab.id
                      ? 'text-black bg-blue-1/10'
                      : 'text-gray-600 hover:bg-blue-1/10 hover:text-black',
                    'whitespace-nowrap py-2 px-3 text-sm font-medium rounded-md'
                  )}
                  aria-current={tab.id === activeTab.id ? 'page' : undefined}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <FortnoxTable fortnoxList={fortnoxList} isLoading={isLoading} />

      <div className="flex overflow-x-auto sm:justify-center pb-16 pt-8">
        {!isLoading && fortnoxList ? (
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={
              !fortnoxList || fortnoxList.total === 0 ? 1 : Math.ceil(fortnoxList.total / 10)
            }
            onPageChange={onPageChange}
            previousLabel="Föregående"
            nextLabel="Nästa"
            showIcons
            theme={theme}
          />
        ) : null}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleSaveCancel}>
        <div className="px-4 pb-4 pt-5">
          <h2 className="text-xl font-semibold leading-6 text-gray-900 pb-4">
            Utför åtgärder?
          </h2>
          <div className="mt-2 text-sm text-gray-800">
            <p className="mb-1"><span className="font-semibold">{listCounts.added}</span> fakturor
            kommer skickas till inkasso. </p>
            <p><span className="font-semibold">{listCounts.ignored}</span> kommer
            att hamna i fliken "Avvaktade".</p>
          </div>
          <div className="mt-8 flex gap-2 justify-end">
            <button
              className="inline-flex w-full justify-center rounded-md bg-white px-8 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={handleSaveCancel}
            >
              Avbryt
            </button>
            <button
              className="inline-flex w-full justify-center px-8 py-2.5 text-sm font-semibold hover:text-blue-1 bg-blue-1  transition-all duration-100 ease-in-out hover:bg-transparent border-2 border-blue-1 border-solid rounded-md cursor-pointer text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
              onClick={handleSaveContinue}
            >
              Utför åtgärder
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const theme = {
  base: "",
  pages: {
    base: "xs:mt-0 mt-2 inline-flex items-center -space-x-px",
    previous: {
      base: "rounded-l-lg border bg-white py-2 px-3 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
    },
    next: {
      base: "rounded-r-lg border bg-white py-2 px-3 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
    },
  },
};
